import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { authApi, settingsApi, billingApi, featuresApi } from '@/services/api';
import type { CompanyFeatures } from '@/services/api';
import { wsService } from '@/services/websocket';
import type { User, LoginRequest, RegisterRequest, CompanySettings } from '@/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  companySettings: CompanySettings | null;
  companyFeatures: CompanyFeatures | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPaidSubscription: boolean;
  checkingBilling: boolean;
  login: (data: LoginRequest) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  updateUser: (updatedUser: User) => void;
  refreshCompanySettings: () => Promise<void>;
  refreshBillingStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [companyFeatures, setCompanyFeatures] = useState<CompanyFeatures | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [checkingBilling, setCheckingBilling] = useState(true);

  const loadCompanySettings = useCallback(async () => {
    try {
      const { data } = await settingsApi.getCompanySettings();
      setCompanySettings(data);
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  }, []);

  const loadCompanyFeatures = useCallback(async () => {
    try {
      const { data } = await featuresApi.getCompanyFeatures();
      setCompanyFeatures(data);
    } catch (error) {
      console.error('Failed to load company features:', error);
    }
  }, []);

  const checkBillingStatus = useCallback(async (companyId?: number | null) => {
    // Empresa 1 (superadmin) não precisa de pagamento
    if (companyId === 1) {
      setHasPaidSubscription(true);
      setCheckingBilling(false);
      return;
    }

    try {
      setCheckingBilling(true);
      const { data } = await billingApi.getBillingStatus();
      // Usa o campo hasPaidSubscription retornado pelo backend
      setHasPaidSubscription(data.hasPaidSubscription === true);
    } catch (error) {
      // Sem assinatura = não pago
      setHasPaidSubscription(false);
    } finally {
      setCheckingBilling(false);
    }
  }, []);

  const refreshBillingStatus = useCallback(async () => {
    if (user?.companyId) {
      await checkBillingStatus(user.companyId);
    }
  }, [user?.companyId, checkBillingStatus]);

  useEffect(() => {
    const loadUser = async () => {
      // Only load user from server if we don't already have one
      // This prevents unnecessary API calls after successful login
      if (token && !user) {
        try {
          const { data } = await authApi.me();
          setUser(data);

          // Load company settings and features
          await loadCompanySettings();
          await loadCompanyFeatures();

          // Check billing status
          await checkBillingStatus(data.companyId);

          // Connect to WebSocket
          if (data.companyId) {
            wsService.connect(data.companyId);
          }
        } catch (error: any) {
          const status = error?.response?.status;
          
          // Only clear token on 401 (unauthorized/expired) - not on other errors
          if (status === 401) {
            localStorage.removeItem('token');
            setToken(null);
          } else {
            // For other errors (network, 500, etc), just finish loading
          }
          setCheckingBilling(false);
        }
      } else if (token && user) {
        // Token exists and user already loaded, just ensure WebSocket is connected
        if (user.companyId && !wsService.isConnected()) {
          wsService.connect(user.companyId);
        }
      } else {
        // No token
        setCheckingBilling(false);
      }
      setIsLoading(false);
    };

    loadUser();
  }, [token]);

  // Ensure billing is checked when user is loaded
  useEffect(() => {
    if (user && token) {
      checkBillingStatus(user.companyId);
    }
  }, [user]);

  const login = useCallback(async (data: LoginRequest) => {
    const response = await authApi.login(data);
    const { user, token } = response.data;

    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);

    // Load company settings after login
    await loadCompanySettings();
    await loadCompanyFeatures();

    // Check billing status
    await checkBillingStatus(user.companyId);

    // Connect to WebSocket
    if (user.companyId) {
      wsService.connect(user.companyId);
    }
  }, [loadCompanySettings, loadCompanyFeatures, checkBillingStatus]);

  // Login with existing token (used for Google OAuth)
  const loginWithToken = useCallback(async (newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);

    // Load user data
    const { data: userData } = await authApi.me();
    setUser(userData);

    // Load company settings after login
    await loadCompanySettings();
    await loadCompanyFeatures();

    // Check billing status
    await checkBillingStatus(userData.companyId);

    // Connect to WebSocket
    if (userData.companyId) {
      wsService.connect(userData.companyId);
    }
  }, [loadCompanySettings, loadCompanyFeatures, checkBillingStatus]);

  const register = useCallback(async (data: RegisterRequest) => {
    const response = await authApi.register(data);
    const { user, token } = response.data;

    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);

    // Load company settings after register
    await loadCompanySettings();
    await loadCompanyFeatures();

    // Novo registro = ainda não pagou
    setHasPaidSubscription(false);
    setCheckingBilling(false);

    // Connect to WebSocket
    if (user.companyId) {
      wsService.connect(user.companyId);
    }
  }, [loadCompanySettings]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCompanySettings(null);
    setCompanyFeatures(null);
    setHasPaidSubscription(false);
    setCheckingBilling(false);
    wsService.disconnect();
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const refreshCompanySettings = useCallback(async () => {
    await loadCompanySettings();
  }, [loadCompanySettings]);

  // Update document title when company settings change (white label)
  useEffect(() => {
    if (companySettings?.systemName) {
      document.title = `${companySettings.systemName} - Sistema de Atendimento WhatsApp`;
    } else {
      document.title = 'Sistema de Atendimento WhatsApp';
    }
  }, [companySettings?.systemName]);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const contextValue = useMemo(() => ({
    user,
    token,
    companySettings,
    companyFeatures,
    isAuthenticated: !!token && !!user,
    isLoading,
    hasPaidSubscription,
    checkingBilling,
    login,
    loginWithToken,
    register,
    logout,
    updateUser,
    refreshCompanySettings,
    refreshBillingStatus,
  }), [
    user,
    token,
    companySettings,
    companyFeatures,
    isLoading,
    hasPaidSubscription,
    checkingBilling,
    login,
    loginWithToken,
    register,
    logout,
    updateUser,
    refreshCompanySettings,
    refreshBillingStatus,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
